function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/**
 * @class CompareFilterSection
 *
 * Numeric comparison controls for a cabinet filter: an operator dropdown plus one or two
 * value inputs, depending on the operator. Shown for filters whose cabinet definition
 * declares `type: 'numericRange'` (those filters have no value list).
 *
 * Typing is debounced like the cabinet search input so each keystroke does not refetch;
 * changing the operator applies immediately.
 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Input, FormGroup } from 'reactstrap';
import { useDebouncedCallback } from 'use-debounce';
import { COMPARE_OPERATORS, DEFAULT_COMPARE_OPERATOR, getCompareOperator, hasInvertedBetweenBounds, toComparableNumber } from '../../../utils/cabinetCompareFilters';
const VALUE_DEBOUNCE_MS = 500;

/**
 * A number input renders nothing for a value it cannot parse, so a bound arriving from a
 * deep link as "10,000" would leave the user an empty box for a filter that is applied.
 * Comparison accepts thousands separators, so the value is shown in the form the input can
 * display rather than being discarded.
 */
const toInputValue = value => {
  const parsed = toComparableNumber(value);
  return parsed === null ? '' : String(parsed);
};
const toDraft = entry => ({
  operator: getCompareOperator(entry === null || entry === void 0 ? void 0 : entry.operator).id,
  value: toInputValue(entry === null || entry === void 0 ? void 0 : entry.value),
  value2: toInputValue(entry === null || entry === void 0 ? void 0 : entry.value2)
});

/** An untouched draft carries no selection, so it clears the filter rather than storing it. */
const toEntry = draft => {
  const untouched = draft.operator === DEFAULT_COMPARE_OPERATOR && draft.value === '' && draft.value2 === '';
  return untouched ? null : _objectSpread({}, draft);
};
const serializeDraft = draft => `${draft.operator}|${draft.value}|${draft.value2}`;
const CompareFilterSection = ({
  title,
  index,
  entry: _entry = null,
  onChange
}) => {
  const [draft, setDraft] = useState(() => toDraft(_entry));
  const {
    inputCount
  } = getCompareOperator(draft.operator);
  const invertedBetween = hasInvertedBetweenBounds(draft);
  const incomingKey = serializeDraft(toDraft(_entry));
  // Tracks what this control last sent up, so the value echoing back through props is not
  // mistaken for an external change and does not clobber in-flight typing.
  const lastEmittedKey = useRef(incomingKey);
  const emit = next => {
    lastEmittedKey.current = serializeDraft(next);
    onChange(toEntry(next));
  };

  // The hook always calls the latest `emit` and drops a pending call once unmounted.
  const emitDebounced = useDebouncedCallback(emit, VALUE_DEBOUNCE_MS);

  // Re-sync on external changes only (Clear button, saved view, deep link).
  useEffect(() => {
    if (incomingKey === lastEmittedKey.current) {
      return;
    }
    lastEmittedKey.current = incomingKey;
    emitDebounced.cancel();
    setDraft(toDraft(_entry));
  }, [_entry, incomingKey, emitDebounced]);
  const handleOperatorChange = event => {
    const operator = event.target.value;
    // Values the new operator has no input for must not linger as stale bounds.
    const {
      inputCount: nextInputCount
    } = getCompareOperator(operator);
    const next = {
      operator,
      value: nextInputCount > 0 ? draft.value : '',
      value2: nextInputCount > 1 ? draft.value2 : ''
    };
    setDraft(next);
    emitDebounced.cancel();
    emit(next);
  };
  const handleValueChange = key => event => {
    const next = _objectSpread(_objectSpread({}, draft), {}, {
      [key]: event.target.value
    });
    setDraft(next);
    emitDebounced(next);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: "t-cf-compare",
    "data-testid": "cabinet-compare-filter"
  }, /*#__PURE__*/React.createElement(FormGroup, null, /*#__PURE__*/React.createElement(Input, {
    type: "select",
    id: `cabinet-compare-operator-${index}`,
    className: "t-cf-compare-operator",
    "aria-label": `${title} comparison`,
    value: draft.operator,
    onChange: handleOperatorChange
  }, COMPARE_OPERATORS.map(({
    id,
    label
  }) => /*#__PURE__*/React.createElement("option", {
    key: id,
    value: id
  }, label)))), inputCount > 0 && /*#__PURE__*/React.createElement(FormGroup, null, /*#__PURE__*/React.createElement(Input, {
    type: "number",
    className: "t-cf-compare-value",
    "aria-label": inputCount > 1 ? `${title} minimum` : `${title} value`,
    placeholder: inputCount > 1 ? 'Minimum' : 'Value',
    value: draft.value,
    onChange: handleValueChange('value'),
    invalid: invertedBetween
  })), inputCount > 1 && /*#__PURE__*/React.createElement(FormGroup, null, /*#__PURE__*/React.createElement(Input, {
    type: "number",
    className: "t-cf-compare-value2",
    "aria-label": `${title} maximum`,
    placeholder: "Maximum",
    value: draft.value2,
    onChange: handleValueChange('value2'),
    invalid: invertedBetween
  })), invertedBetween && /*#__PURE__*/React.createElement("div", {
    className: "text-danger small",
    role: "alert"
  }, "Minimum must be less than or equal to maximum"));
};
CompareFilterSection.propTypes = {
  title: PropTypes.string.isRequired,
  index: PropTypes.string.isRequired,
  entry: PropTypes.shape({
    operator: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value2: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  onChange: PropTypes.func.isRequired
};
export default CompareFilterSection;